import {
  useMutation,
  type QueryKey,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { FormRecord } from '@pairy/sheard/types';

import { ApiErrorResponse, ApiResponse } from '../../../constant/api';
import { api } from '../../../utils/api';

interface PostCtaFormRequest {
  historyId: string;
  formRecord: FormRecord[];
  code?: string;
}

interface UsePostCtaFormInvalidationParams extends PostCtaFormRequest {
  onSuccess?: () => void;
  onError?: () => void;
}

export const postCtaForm = (request: PostCtaFormRequest) => {
  return api.post<ApiResponse<undefined>>('/v2/viewer/submit/form', {
    ...request,
  });
};

const usePostCtaForm = <TError = AxiosError<ApiErrorResponse>>(
  options?: UseMutationOptions<
    AxiosResponse<ApiResponse<undefined>>,
    TError,
    PostCtaFormRequest,
    QueryKey
  >,
): UseMutationResult<
  AxiosResponse<ApiResponse<undefined>>,
  TError,
  PostCtaFormRequest,
  QueryKey
> => {
  return useMutation(postCtaForm, options);
};

const usePostCtaFormWithInvalidation = () => {
  const queryClient = useQueryClient();

  const { mutate: postCtaFormMutate } = usePostCtaForm();

  const deleteFormHandler = ({
    historyId,
    formRecord,
    code,
    onSuccess,
    onError,
  }: UsePostCtaFormInvalidationParams): void => {
    postCtaFormMutate(
      {
        historyId,
        formRecord,
        code,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: ['getBulkFileRedirect'],
          });

          onSuccess && onSuccess();
        },
        onError: () => {
          onError && onError();
        },
      },
    );
  };
  return { mutate: deleteFormHandler };
};

export default usePostCtaFormWithInvalidation;
